import { useMutation } from '@apollo/client'
import {
  DatePicker,
  Form,
  NumberInput,
  PhoneInput,
  TextArea,
  TextInput,
  useForm,
} from '@faceup/form'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { useNavigate, useSearchParams } from '@faceup/router'
import { blackVariant } from '@faceup/ui'
import { Flex, Typography, useMessage } from '@faceup/ui-base'
import {
  DEFAULT_COUNTRY,
  FormItemType,
  getTranslation,
  isEmail,
  isWholeNumber,
  type Language,
  SHORT_STRING_MAX_LENGTH,
  SURVEY_SOURCE_PARAM_NAME,
  SurveySource,
} from '@faceup/utils'
import { type InferType, yup } from '@faceup/validation'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useEffect, useRef } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import {
  SurveyMultipleChoice,
  SurveyRateChoice,
  SurveyScaleChoice,
  SurveySingleChoice,
} from '../../Components/Survey'
import { SurveyFileUpload } from '../../Components/Survey/SurveyFileUpload'
import { SurveyMatrixChoice } from '../../Components/Survey/SurveyMatrixChoice'
import { useIsFilledSurvey } from '../../hooks/useIsFilledSurvey'
import useReportAuth from '../../utils/useReportAuth'

const fragments = {
  CreateSurveyForm_survey: graphql(`
    fragment CreateSurveyForm_survey on PublicSurvey {
      shortLink
      formId
      config {
        id
        formItems(page: 0, rowsPerPage: 123456) {
          id
          type
          isRequired(channelId: $channelIdHack)
          order(channelId: $channelIdHack)
          labelTranslations {
            translation
            language
          }
          hintTranslations {
            translation
            language
          }
          maxResponses
          minResponses
          hasOtherOption
          maxLength
          options {
            id
            order
            labelTranslations {
              translation
              language
            }
          }
          scaleStartLabelTranslations {
            translation
            language
          }
          scaleEndLabelTranslations {
            translation
            language
          }
          matrixConfig {
            isMultipleSelectionAllowed
            options {
              id
              labelTranslations {
                translation
                language
              }
            }
            questions {
              id
              labelTranslations {
                translation
                language
              }
            }
          }
          ...UseCreateYupSchema_formItem
        }
      }
    }
  `),
  UseCreateYupSchema_formItem: graphql(`
    fragment UseCreateYupSchema_formItem on FormItem {
      id
      type
      isRequired(channelId: $channelIdHack)
      maxLength
      maxResponses
      minResponses
      hasOtherOption
      options {
        id
        labelTranslations {
          translation
          language
        }
      }
      matrixConfig {
        isMultipleSelectionAllowed
        options {
          id
        }
        questions {
          id
        }
      }
    }
  `),
  CreateSurveyForm_institution: graphql(`
    fragment CreateSurveyForm_institution on PublicCompany {
      id
      country
    }
  `),
  CreateSurveyForm_system: graphql(`
    fragment CreateSurveyForm_system on System {
      id
      publicKey
    }
  `),
}

const mutations = {
  createSubmission: graphql(`
    mutation CreateSurveySubmission($input: CreateSurveySubmissionInput!) {
      createSurveySubmission(input: $input) {
        createdSubmission {
          id
        }
      }
    }
  `),
}

const messages = defineMessages('followUp.pages.survey.createSurveyForm', [
  { id: 'surveyNotSent', defaultMessage: 'Answers from preview are not saved' },
  { id: 'questionsTitle', defaultMessage: 'Questions' },
  { id: 'isRequired', defaultMessage: 'This field is required' },
  { id: 'fillAllRequiredFields', defaultMessage: 'Please fill all required fields' },
  { id: 'invalidWholeNumber', defaultMessage: 'Invalid number. Please insert a whole number.' },
  { id: 'minLimit', defaultMessage: 'Please select more options', values: { count: true } },
  { id: 'maxLimit', defaultMessage: 'Please select less options', values: { count: true } },
] as const)

type FormItems = FragmentType<typeof fragments.UseCreateYupSchema_formItem>[]

const useCreateYupSchema = (_formItems: FormItems) => {
  const formItems = getFragmentData(fragments.UseCreateYupSchema_formItem, _formItems)
  const { getValidationForFormItem } = useValidationForFormItem()
  const schema = formItems.reduce<Record<string, ReturnType<typeof getValidationForFormItem>>>(
    (accSchema, formItem) => {
      const { id } = formItem
      const validator = getValidationForFormItem(formItem)
      return {
        ...accSchema,
        [id]: validator,
      }
    },
    {}
  )

  return yup.object().shape(schema)
}

type CreateSurveyFormProps = {
  isPreview: boolean
  survey: FragmentType<typeof fragments.CreateSurveyForm_survey>
  institution: FragmentType<typeof fragments.CreateSurveyForm_institution>
  surveyId: string
  surveyDefaultLanguage: Language
}

/**
 * CreateSubmissionForm actually.
 */
export const CreateSurveyForm = ({
  isPreview,
  survey: _survey,
  institution: _institution,
  surveyId,
  surveyDefaultLanguage,
}: CreateSurveyFormProps) => {
  const { logout } = useReportAuth()
  const getTimeElapsed = useTimeElapsed()
  const navigate = useNavigate()
  const [, setIsFilledSurvey] = useIsFilledSurvey(surveyId)

  const institution = getFragmentData(fragments.CreateSurveyForm_institution, _institution)
  const survey = getFragmentData(fragments.CreateSurveyForm_survey, _survey)
  const message = useMessage()
  const { formatMessage } = useIntl()

  const formItems = survey.config.formItems ?? []
  const schema = useCreateYupSchema(formItems)

  const form = useForm({
    schema,
    afterSubmit: 'persistValues',
    defaultValues: formItems.reduce(
      (acc, formItem) => ({
        ...acc,
        [formItem.id]: {
          type: formItem.type,
          value: undefined,
          otherValue: undefined,
        },
      }),
      {}
    ) as InferType<typeof schema>,
  })

  const surveySource = useSurveySource()

  const [createSubmission] = useMutation(mutations.createSubmission)

  // biome-ignore lint/correctness/useExhaustiveDependencies: message and formatMessage are stable
  useEffect(() => {
    if (Object.keys(form.formState.errors).length > 0 && form.formState.isSubmitted) {
      message.error(formatMessage(messages.fillAllRequiredFields))
    }
  }, [form.formState.errors, form.formState.isSubmitted])

  return (
    <Flex vertical gap={64} style={{ width: '100%' }} data-test='create-survey-form'>
      <Flex justify='center'>
        <Typography.Title level={3}>
          <FormattedMessage message={messages.questionsTitle} />
        </Typography.Title>
      </Flex>
      <Form
        variant={blackVariant}
        buttonsPosition='under-center'
        submitButtonText='send'
        isUnsavedAlertDisabled={isPreview}
        form={form}
        isSuccessMessageDisplayed={false}
        onSubmit={async values => {
          if (isPreview) {
            navigate(routes => routes.sentSurvey(), {
              search: '?preview=',
            })

            message.warning(formatMessage(messages.surveyNotSent))

            return true
          }

          const result = await createSubmission({
            variables: {
              input: {
                formId: survey.formId,
                answers: Object.entries(values)
                  .filter(([_formItemId, formValue]) => {
                    const { type, value } = formValue
                    const otherValue = 'otherValue' in formValue ? formValue.otherValue : undefined

                    if (otherValue) {
                      return true
                    }

                    if (value === undefined || value === null || value === '') {
                      return false
                    }

                    if (Array.isArray(value) && value.length === 0) {
                      return false
                    }

                    if (type === FormItemType.Matrix) {
                      const matrixValue = value as Record<string, string[]>
                      const hasAnyValue = Object.values(matrixValue).some(
                        optionIds => optionIds && optionIds.length > 0
                      )
                      return hasAnyValue
                    }

                    return true
                  })
                  .map(([key, formValue]) => {
                    const { type, value } = formValue
                    const otherValue = 'otherValue' in formValue ? formValue.otherValue : undefined
                    const formItem = formItems.find(item => item.id === key)

                    if (formItem?.hasOtherOption && otherValue) {
                      // Handle other option for both Select and MultiSelect
                      if (type === FormItemType.MultiSelect) {
                        // MultiSelect: send selected options and other text value
                        return {
                          formItemId: key,
                          optionIds: value ?? [],
                          textValue: otherValue,
                        }
                      } else if (type === FormItemType.Select) {
                        // Select: send selected option if any, and other text value
                        return {
                          formItemId: key,
                          optionIds: value ? [value] : [],
                          textValue: otherValue,
                        }
                      }
                    }
                    if (type === FormItemType.Matrix) {
                      const matrixValues = Object.entries(value as Record<string, string[]>).map(
                        ([questionId, optionIds]) => ({
                          questionId,
                          optionIds: optionIds ?? [],
                        })
                      )
                      return {
                        formItemId: key,
                        matrixValues,
                      }
                    }
                    if (type === FormItemType.File) {
                      return {
                        formItemId: key,
                        files: Array.isArray(value) ? value : value ? [value] : [],
                      }
                    }

                    if (
                      type === FormItemType.SimpleText ||
                      type === FormItemType.MultilineText ||
                      type === FormItemType.Date ||
                      type === FormItemType.Email ||
                      type === FormItemType.PhoneNumber ||
                      type === FormItemType.Number
                    ) {
                      return {
                        formItemId: key,
                        textValue: value ? String(value) : null,
                      }
                    }

                    return {
                      formItemId: key,
                      optionIds: Array.isArray(value) ? value : value ? [value] : [],
                    }
                  }),
                durationInMs: getTimeElapsed(),
                surveySource,
              },
            },
          })
          if (result.errors) {
            return false
          }

          setIsFilledSurvey(true)
          await logout()

          navigate(routes => routes.sentSurvey())

          return true
        }}
      >
        <Flex vertical gap={64} className='mb-6 text-start'>
          {formItems.map(formItem => {
            const { id, type, options, maxResponses, hasOtherOption } = formItem
            const label = getTranslation(
              formItem.labelTranslations,
              surveyDefaultLanguage,
              surveyDefaultLanguage
            )
            const hint = getTranslation(
              formItem.hintTranslations ?? [],
              surveyDefaultLanguage,
              surveyDefaultLanguage
            )
            const optionLabels = options.map(option => ({
              label: option.labelTranslations[0]?.translation ?? '',
              value: option.id,
            }))
            const startLabel = getTranslation(
              formItem.scaleStartLabelTranslations ?? [],
              surveyDefaultLanguage,
              surveyDefaultLanguage
            )
            const endLabel = getTranslation(
              formItem.scaleEndLabelTranslations ?? [],
              surveyDefaultLanguage,
              surveyDefaultLanguage
            )
            const matrixConfig = formItem.matrixConfig

            switch (type) {
              case FormItemType.SimpleText:
                return (
                  <TextInput
                    key={id}
                    name={`${id}.value`}
                    label={label}
                    hint={hint}
                    control={form.control}
                  />
                )
              case FormItemType.MultilineText:
                return (
                  <TextArea
                    name={`${id}.value`}
                    key={id}
                    label={label}
                    hint={hint}
                    control={form.control}
                    rows={4}
                  />
                )
              case FormItemType.Select:
                return (
                  <SurveySingleChoice
                    key={id}
                    name={`${id}`}
                    label={label}
                    hint={hint}
                    control={form.control}
                    options={optionLabels}
                    hasOtherOption={hasOtherOption}
                  />
                )
              case FormItemType.MultiSelect:
                return (
                  <SurveyMultipleChoice
                    key={id}
                    name={`${id}`}
                    label={label}
                    hint={hint}
                    control={form.control}
                    options={optionLabels}
                    maxResponses={maxResponses}
                    hasOtherOption={hasOtherOption}
                  />
                )
              case FormItemType.Scale:
                return (
                  <SurveyScaleChoice
                    key={id}
                    name={`${id}.value`}
                    label={label}
                    hint={hint}
                    control={form.control}
                    options={optionLabels}
                    startLabel={startLabel}
                    endLabel={endLabel}
                  />
                )
              case FormItemType.Date:
                return (
                  <DatePicker
                    key={id}
                    name={`${id}.value`}
                    label={label}
                    hint={hint}
                    control={form.control}
                    onChange={date => {
                      const formattedDate = date ? date.format('YYYY-MM-DD') : ''
                      form.setValue(id, {
                        type: FormItemType.Date,
                        value: formattedDate,
                      })
                    }}
                  />
                )
              case FormItemType.Email:
                return (
                  <TextInput
                    type='email'
                    key={id}
                    name={`${id}.value`}
                    label={label}
                    hint={hint}
                    control={form.control}
                  />
                )
              case FormItemType.File:
                return (
                  <SurveyFileUpload
                    key={id}
                    name={`${id}.value`}
                    fieldId={id}
                    label={label}
                    hint={hint}
                    control={form.control}
                  />
                )
              case FormItemType.PhoneNumber:
                return (
                  <PhoneInput
                    key={id}
                    name={`${id}.value`}
                    label={label}
                    hint={hint}
                    country={institution.country ?? DEFAULT_COUNTRY}
                    control={form.control}
                  />
                )
              case FormItemType.Number:
                return (
                  <NumberInput
                    key={id}
                    name={`${id}.value`}
                    label={label}
                    hint={hint}
                    control={form.control}
                    type='number'
                    stringMode
                  />
                )
              case FormItemType.Rating:
                return (
                  <SurveyRateChoice
                    key={id}
                    name={`${id}.value`}
                    label={label}
                    hint={hint}
                    control={form.control}
                    options={optionLabels}
                  />
                )
              case FormItemType.Matrix:
                return matrixConfig ? (
                  <SurveyMatrixChoice
                    key={id}
                    name={`${id}.value`}
                    label={label}
                    hint={hint}
                    control={form.control}
                    isMultipleSelectionAllowed={matrixConfig.isMultipleSelectionAllowed}
                    options={matrixConfig.options.map(option => ({
                      id: option.id,
                      label: getTranslation(
                        option.labelTranslations,
                        surveyDefaultLanguage,
                        surveyDefaultLanguage
                      ),
                    }))}
                    questions={matrixConfig.questions.map(question => ({
                      id: question.id,
                      label: getTranslation(
                        question.labelTranslations,
                        surveyDefaultLanguage,
                        surveyDefaultLanguage
                      ),
                    }))}
                  />
                ) : (
                  'No config for matrix input'
                )
              default:
                return <>Implement {type}</>
            }
          })}
        </Flex>
      </Form>
    </Flex>
  )
}

const surveySourceSchema = yup.string().oneOf(Object.values(SurveySource)).required()

// hook that extracts the survey source from the URL
const useSurveySource = (): SurveySource => {
  const [params] = useSearchParams()

  const surveySourceValue = params.get(SURVEY_SOURCE_PARAM_NAME) ?? SurveySource.Direct

  try {
    return surveySourceSchema.validateSync(surveySourceValue)
  } catch {
    return SurveySource.Unknown
  }
}

// hook that can be used to get elapsed time after a component was mounted
const useTimeElapsed = () => {
  const startTime = useRef(Date.now())

  const getTimeElapsed = () => {
    return Date.now() - startTime.current
  }

  return getTimeElapsed
}

const useValidationForFormItem = () => {
  const { formatMessage } = useIntl()
  const getMatrixSchema = SurveyMatrixChoice.useSchema()

  const getValidationForFormItem = (
    formItem: ResultOf<typeof fragments.UseCreateYupSchema_formItem>
  ) => {
    const {
      type,
      isRequired,
      maxLength,
      maxResponses,
      minResponses,
      hasOtherOption,
      matrixConfig,
    } = formItem
    switch (type) {
      case FormItemType.MultilineText: {
        let multilineTextSchema = yup.string()
        if (maxLength) {
          multilineTextSchema = multilineTextSchema.max(maxLength)
        }
        if (isRequired) {
          multilineTextSchema = multilineTextSchema.required()
        }

        return yup.object({
          type: yup.string().oneOf([FormItemType.MultilineText]).required(),
          value: multilineTextSchema,
        })
      }
      case FormItemType.SimpleText: {
        let simpleTextSchema = yup.string().max(SHORT_STRING_MAX_LENGTH)
        if (isRequired) {
          simpleTextSchema = simpleTextSchema.required()
        }
        return yup.object({
          type: yup.string().oneOf([FormItemType.SimpleText]).required(),
          value: simpleTextSchema,
        })
      }
      case FormItemType.Date: {
        let dateSchema = yup.string()
        if (isRequired) {
          dateSchema = dateSchema.required()
        }
        return yup.object({
          type: yup.string().oneOf([FormItemType.Date]).required(),
          value: dateSchema,
        })
      }
      case FormItemType.Email: {
        let emailSchema = yup.string().test(value => (value ? isEmail(value) : true))
        if (isRequired) {
          emailSchema = emailSchema.required()
        }
        return yup.object({
          type: yup.string().oneOf([FormItemType.Email]).required(),
          value: emailSchema,
        })
      }
      case FormItemType.PhoneNumber: {
        let phoneSchema = yup.string()
        if (isRequired) {
          phoneSchema = phoneSchema.required()
        }
        return yup.object({
          type: yup.string().oneOf([FormItemType.PhoneNumber]).required(),
          value: phoneSchema,
        })
      }
      case FormItemType.Number: {
        let numberSchema = yup
          .string()
          .test(
            'invalid-whole-number',
            formatMessage(messages.invalidWholeNumber),
            value => !value || isWholeNumber(value)
          )
        if (isRequired) {
          numberSchema = numberSchema.required()
        }
        return yup.object({
          type: yup.string().oneOf([FormItemType.Number]).required(),
          value: numberSchema.transform(value => (value ? value : undefined)),
        })
      }
      case FormItemType.Scale:
        return yup.object({
          type: yup.string().oneOf([FormItemType.Scale]).required(),
          value: isRequired
            ? yup
                .string()
                .required()
                .oneOf(formItem.options.map(o => o.id))
            : yup.string().oneOf(formItem.options.map(o => o.id)),
        })
      case FormItemType.Select:
        return yup
          .object({
            type: yup.string().oneOf([FormItemType.Select]).required(),
            value: isRequired
              ? hasOtherOption
                ? yup.string().trim().required()
                : yup
                    .string()
                    .required()
                    .oneOf(
                      formItem.options.map(o => o.id),
                      'Invalid option'
                    )
              : hasOtherOption
                ? yup.string().trim()
                : yup.string().oneOf(
                    formItem.options.map(o => o.id),
                    'Invalid option'
                  ),
            otherValue: yup.string().trim(),
          })
          .test('select-other-required', formatMessage(messages.isRequired), obj => {
            if (!isRequired || !hasOtherOption) {
              return true
            }
            return Boolean(obj.value ?? obj.otherValue)
          })
      case FormItemType.MultiSelect: {
        const elementSchema = hasOtherOption
          ? yup
              .string()
              .required() // allow free-text
          : yup
              .string()
              .oneOf(formItem.options.map(o => o.id))
              .required()

        return yup
          .object({
            type: yup.string().oneOf([FormItemType.MultiSelect]).required(),
            value: isRequired
              ? yup
                  .array()
                  .of(elementSchema)
                  .required()

                  .min(
                    (minResponses ?? 1) - (hasOtherOption ? 1 : 0),
                    formatMessage(messages.minLimit, { values: { count: minResponses ?? 1 } })
                  )
                  .max(
                    maxResponses ?? Number.POSITIVE_INFINITY,
                    formatMessage(messages.maxLimit, { values: { count: maxResponses ?? 0 } })
                  )
              : yup
                  .array()
                  .of(elementSchema)
                  .test(
                    'min-limit',
                    formatMessage(messages.minLimit, { values: { count: minResponses ?? 1 } }),
                    values => {
                      const isValid =
                        minResponses === null ||
                        values?.length === 0 ||
                        (!!values?.length && values.length >= minResponses)
                      return isValid
                    }
                  )
                  .test(
                    'max-limit',
                    formatMessage(messages.maxLimit, { values: { count: maxResponses ?? 0 } }),
                    values => {
                      const isValid =
                        maxResponses === null ||
                        values?.length === 0 ||
                        (!!values?.length && values.length <= maxResponses)
                      return isValid
                    }
                  ),
            otherValue: yup.string().trim(),
          })
          .test('multiselect-other-required', formatMessage(messages.isRequired), obj => {
            if (!isRequired || !hasOtherOption) {
              return true
            }
            const selectedCount = (obj.value?.length ?? 0) + (obj.otherValue ? 1 : 0)
            return selectedCount > 0
          })
          .test(
            'multiselect-other-min-limit',
            formatMessage(messages.minLimit, { values: { count: minResponses ?? 1 } }),
            obj => {
              if (!hasOtherOption) {
                return true
              }
              const selectedCount = (obj.value?.length ?? 0) + (obj.otherValue ? 1 : 0)
              return (
                minResponses === null || selectedCount === 0 || selectedCount >= (minResponses ?? 1)
              )
            }
          )
          .test(
            'multiselect-other-max-limit',
            formatMessage(messages.maxLimit, { values: { count: maxResponses ?? 0 } }),
            obj => {
              if (!hasOtherOption) {
                return true
              }
              const selectedCount = (obj.value?.length ?? 0) + (obj.otherValue ? 1 : 0)
              return maxResponses === null || selectedCount === 0 || selectedCount <= maxResponses
            }
          )
      }
      case FormItemType.Rating:
        return yup.object({
          type: yup.string().oneOf([FormItemType.Rating]).required(),
          value: isRequired
            ? yup
                .string()
                .required()
                .oneOf(formItem.options.map(o => o.id))
            : yup.string().oneOf(formItem.options.map(o => o.id)),
        })
      case FormItemType.File: {
        const fileSchema = yup.mixed((input): input is File => input instanceof File)
        return yup.object({
          type: yup.string().oneOf([FormItemType.File]).required(),
          value: isRequired ? fileSchema.required() : fileSchema,
        })
      }
      case FormItemType.Matrix: {
        if (!matrixConfig) {
          throw new Error('matrixConfig does not exists')
        }
        const matrixSchema = getMatrixSchema(matrixConfig, isRequired)
        return yup.object({
          type: yup.string().oneOf([FormItemType.Matrix]).required(),
          value: isRequired ? matrixSchema.required() : matrixSchema,
        })
      }
      default: {
        throw new Error(`Unsupported form item type: ${type}`)
      }
    }
  }
  return {
    getValidationForFormItem,
  }
}
